import { useState, useEffect } from "react";
import { clamp, hash01, pickClassicStyle } from "./chunk-0.jsx";
export function ClassicEyes({
  emotion,
  accent,
  poke = 0
}) {
  const s = pickClassicStyle(emotion, accent);
  const openness = clamp(s.openness ?? 0.9, 0.12, 1.1);
  const lid = clamp(1 - openness, 0, 0.9);
  const pupilScale = clamp(s.pupil ?? 1.0, 0.5, 1.1);
  const jiggle = Boolean(s.jiggle);
  const [blinkPulse, setBlinkPulse] = useState(false);
  useEffect(() => {
    if (!poke) return;
    setBlinkPulse(true);
    const t = setTimeout(() => setBlinkPulse(false), 260);
    return () => clearTimeout(t);
  }, [poke]);
  return <div className={`w-full h-full flex items-center justify-center ${jiggle ? 'animate-[wiggle_0.5s_ease-in-out_infinite]' : ''}`}>
      <div className="relative w-full h-full flex items-center justify-center gap-[10%]">
        <ClassicEye lid={lid} pupilScale={pupilScale} iris={s.iris} brow={s} side="left" wink={Boolean(s.wink)} blinkPulse={blinkPulse} seedKey={`classic:${emotion}`} />
        <ClassicEye lid={lid} pupilScale={pupilScale} iris={s.iris} brow={s} side="right" wink={false} blinkPulse={blinkPulse} seedKey={`classic:${emotion}`} />
        {s.glasses ? <div className="absolute inset-x-[10%] top-[26%] h-[24%] pointer-events-none">
            <div className="absolute left-[10%] top-1/2 w-[32%] h-[74%] -translate-y-1/2 rounded-2xl bg-black/70 border border-white/15" />
            <div className="absolute right-[10%] top-1/2 w-[32%] h-[74%] -translate-y-1/2 rounded-2xl bg-black/70 border border-white/15" />
            <div className="absolute left-1/2 top-1/2 w-[12%] h-[10%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/10" />
          </div> : null}
        {s.blush ? <>
            <div className="absolute left-[22%] bottom-[16%] w-[10%] h-[6%] rounded-full bg-pink-500/25 blur-[1px] pointer-events-none" />
            <div className="absolute right-[22%] bottom-[16%] w-[10%] h-[6%] rounded-full bg-pink-500/25 blur-[1px] pointer-events-none" />
          </> : null}
        {s.tear ? <div className="absolute left-[28%] bottom-[10%] w-[5%] h-[14%] bg-sky-300/70 rounded-b-full rounded-t-[45%] rotate-[8deg] pointer-events-none" /> : null}
        {s.drool ? <div className="absolute left-1/2 bottom-[6%] w-[4%] h-[14%] -translate-x-1/2 bg-cyan-200/70 rounded-b-full rounded-t-[45%] pointer-events-none" /> : null}
      </div>
      <style>{`
        @keyframes wiggle{0%,100%{transform:translateY(0)}50%{transform:translateY(1px)}}
        @keyframes classicGaze{
          0%,100%{transform:translate3d(0,0,0)}
          22%{transform:translate3d(-7%,3%,0)}
          45%{transform:translate3d(6%,-2%,0)}
          70%{transform:translate3d(-3%,-6%,0)}
        }
        @keyframes classicBlink{
          0%,92%,100%{transform:scaleY(0)}
          94%{transform:scaleY(1)}
          96%{transform:scaleY(0)}
        }
      `}</style>
    </div>;
}
export function ClassicEye({
  lid,
  pupilScale,
  iris,
  brow,
  side,
  wink,
  blinkPulse,
  seedKey
}) {
  const isLeft = side === 'left';
  const browColor = brow.browColor || 'rgba(255,255,255,0.85)';
  const browRotate = (brow.brow ?? 0) * (isLeft ? -1 : 1);
  const browY = brow.browDown ? 'translateY(6px)' : 'translateY(0px)';
  const asym = Boolean(brow.asymBrow);
  const browExtra = asym ? isLeft ? -8 : 8 : 0;
  const leftClose = wink ? 0.95 : lid;
  const close = clamp(isLeft ? leftClose : lid, 0, 0.95);
  const seed = hash01(seedKey || `classic:${side}`);
  const blinkDurationS = 2.8 + seed * 2.0;
  const gazeDurationS = 3.6 + seed * 2.2;
  const blinkDuration = `${blinkDurationS.toFixed(2)}s`;
  const gazeDuration = `${gazeDurationS.toFixed(2)}s`;
  const blinkDelay = `${(-seed * blinkDurationS).toFixed(2)}s`;
  const gazeDelay = `${(-seed * gazeDurationS).toFixed(2)}s`;
  return <div className="relative w-[36%] h-[56%]">
      <div className="absolute -top-[28%] left-[12%] right-[12%] h-[12%]">
        <div className="h-full w-full rounded-full" style={{
        background: browColor,
        transform: `rotate(${browRotate + browExtra}deg) ${browY}`,
        opacity: 0.95
      }} />
      </div>

      <div className="absolute inset-0 rounded-full bg-white/95 overflow-hidden">
        <div className="absolute left-1/2 top-1/2 w-[62%] aspect-square rounded-full" style={{
        background: iris || '#111827',
        transform: 'translate(-50%,-50%)'
      }}>
        <div className="absolute inset-0" style={{
          animationName: 'classicGaze',
          animationTimingFunction: 'ease-in-out',
          animationIterationCount: 'infinite',
          animationDuration: gazeDuration,
          animationDelay: gazeDelay,
          willChange: 'transform'
        }}>
            <div className="absolute left-1/2 top-1/2 rounded-full bg-black" style={{
            width: `${34 * pupilScale}%`,
            height: `${34 * pupilScale}%`,
            transform: 'translate(-50%,-50%)'
          }} />
            <div className="absolute left-[18%] top-[18%] w-[16%] aspect-square rounded-full bg-white/60" />
          </div>
        </div>

        <div className="absolute left-0 right-0 top-0 bg-black" style={{
        height: `${close * 50}%`
      }} />
        <div className="absolute left-0 right-0 bottom-0 bg-black" style={{
        height: `${close * 50}%`
      }} />

        <div className="absolute inset-0 bg-black origin-center pointer-events-none" style={{
        transform: 'scaleY(0)',
        animationName: 'classicBlink',
        animationTimingFunction: 'ease-in-out',
        animationIterationCount: 'infinite',
        animationDuration: blinkDuration,
        animationDelay: blinkDelay,
        willChange: 'transform'
      }} />
        <div className={`absolute inset-0 bg-black origin-center pointer-events-none transition-transform duration-150 ease-in-out ${blinkPulse ? 'scale-y-100' : 'scale-y-0'}`} />
      </div>
    </div>;
}
export function normalizeChamiiVariant(variant) {
  const v = String(variant || '').trim().toLowerCase();
  const allowed = new Set(['macaron', 'sparkle', 'sticker', 'jelly', 'kitty', 'boba', 'rainbow', 'choco', 'mochi', 'neon', 'dreamy', 'pixel']);
  if (allowed.has(v)) return v;
  return 'macaron';
}
